import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';

const AuthCallbackPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading, session } = useAuth();
  const { showSuccess, showError } = useToast();
  const [authVerified, setAuthVerified] = useState(false);
  const successShownRef = useRef(false);

  // Check for OAuth errors in URL parameters
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const error = urlParams.get('error');
    const errorDescription = urlParams.get('error_description');
    
    if (error) {
      console.error('OAuth Error:', { error, errorDescription });
      showError(`Authentication failed: ${errorDescription || error}`);
      
      // Show detailed error info for debugging
      console.log('Full error details:', {
        error,
        errorDescription,
        urlParams: Object.fromEntries(urlParams.entries()),
        currentUrl: window.location.href
      });
      
      // Redirect back to auth page after a delay
      setTimeout(() => {
        navigate('/auth', { replace: true });
      }, 3000);
      return;
    }
  }, [location, showError, navigate]);

  useEffect(() => {
    // Don't proceed if there's an OAuth error
    const urlParams = new URLSearchParams(location.search);
    if (urlParams.get('error')) {
      return;
    }

    // Simple auth verification - no complex retry logic
    if (loading) {
      console.log('Auth still loading, waiting...');
      return;
    }

    if (!user || !user.id) {
      console.log('No user found, auth not established');
      return;
    }

    if (!session?.access_token) {
      console.log('No access token found, waiting for session...');
      return;
    }

    // Authentication is verified
    console.log('Authentication data verified:', {
      userId: user.id,
      hasToken: !!session.access_token,
      tokenPreview: session.access_token.substring(0, 20) + '...'
    });

    // Show success and redirect (only once)
    if (!successShownRef.current) {
      setAuthVerified(true);
      successShownRef.current = true;
      showSuccess('Authentication successful! Redirecting to dashboard...');
      
      // Small delay to show success message
      setTimeout(() => {
        navigate('/dashboard', { replace: true });
      }, 1000);
    }

  }, [user, loading, session, navigate, showSuccess, location]);

  // Show error state if there's an OAuth error
  const urlParams = new URLSearchParams(location.search);
  const error = urlParams.get('error');
  
  if (error) {
    return (
      <div className="min-h-screen bg-base-200 flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl mb-4">❌</div>
          <h2 className="text-xl font-semibold mb-2 text-error">Authentication Failed</h2>
          <p className="text-base-content/70 mb-4">
            {urlParams.get('error_description') || 'An error occurred during authentication'}
          </p>
          <p className="text-sm text-base-content/50 mb-4">
            Error Code: {error}
          </p>
          <button 
            className="btn btn-primary"
            onClick={() => navigate('/auth', { replace: true })}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-base-200 flex items-center justify-center">
      <div className="text-center">
        <div className="loading loading-spinner loading-lg mb-4"></div>
        <h2 className="text-xl font-semibold mb-2">Completing Authentication</h2>
        <p className="text-base-content/70 mb-4">
          Please wait while we verify your login...
        </p>

      </div>
    </div>
  );
};

export default AuthCallbackPage; 